import React, { useState } from "react";
import {
    ActivateFeatureButton,
    Button,
    DataTable,
    DataTableEditActionButton,
    DataTableViewActionButton,
    DataTableCell,
    DataTableContainer,
    DataTableHeaderCell,
    FlexContainer,
    LottieLoadingIndicator,
    PortalSearchInput,
    PortalSelect,
    PortalTableActions,
    PortalTableControls,
    UpperControls
} from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import styled from "styled-components";
import moment from "moment";
import { PERK_STATUS, PERK_TYPE } from "src/constants/perks";
import InsightModal from "./InsightModal";
import UpArrow from "src/img/employeeList/SortUpArrow.svg";
import DownArrow from "src/img/employeeList/SortDownArrow.svg";
import { TableRowCell } from "src/components/TableRowCell";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const DeleteButton = styled(Button)`
    border-radius: 8px !important;
    border: none;
    height: 42px;
    width: 10.75rem;
    color: #fff;
    background-color: #ff7171;
    box-sizing: border-box;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0 !important;

    @media (max-width: 1440px) {
        width: 9.75rem;
    }

    @media (max-width: 760px) {
        width: 100%;
    }
`;

const PerksUpperControls = styled(UpperControls)`
    align-items: flex-start;
`;

const PerkImgContainer = styled.div`
    width: 83px;
    height: 47px;
    display: flex;
    justify-content: center;
    border: 1px solid var(--calendar-border, #D8DEEC);
    border-radius: 8px;
`;

const PerkImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    align-self: center;
`;

const RecipientLabel = styled.p`
    margin: 0;
`;

const SORTABLE_COLUMNS = {
    COMPANY_NAME: "companyName",
    END_DATE: "endDate"
};

const SORT_DIRECTION = {
    ASC: "asc",
    DESC: "desc"
};

const PerksTable = ({
    user,
    perks,
    onHandleFilterByType,
    handleSearchChange,
    handleFilterByStatus,
    filterType,
    searchQuery,
    filterStatus,
    isFetching,
    allPerksChecked,
    handleCheckAllPerks,
    checkedPerks,
    handleCheckPerk,
    handleShowDeleteDialog,
    handleFormVisible,
    handleEditPerk
}) => {

    const [showInsightsModal, setShowInsightsModal] = useState(false);
    const [selectedPerk, setSelectedPerk] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: SORTABLE_COLUMNS.END_DATE, direction: SORT_DIRECTION.DESC });

    const handleShowInsightModal = perk => {
        setSelectedPerk(perk);
        setShowInsightsModal(true);
    };

    const handleCloseInsightModal = () => {
        setSelectedPerk(null);
        setShowInsightsModal(false);
    };

    const formatExpiryDate = date => {
        const currentDate = moment().startOf("day");
        const expirationDate = moment(date);
        if (currentDate.isAfter(expirationDate)) {
            return (<span style={{ color: "#ff4646", fontWeight: 700 }}>{expirationDate.format("MMMM D, YYYY")}</span>);
        }
        return expirationDate.format("MMMM D, YYYY");
    };

    const promoType = perk => {
        return perk.promoCode ? "Text" : "Image";
    };

    const renderExtraRows = () => {
        const minRows = 10;
        const noOfRowsToRender = minRows - perks.length;
        const extraRows = [];

        for (let i = 0; i < noOfRowsToRender; i++) {
            extraRows.push(
                <tr key={`empty-${i}`} className="placeholder-row">
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                </tr>
            );
        }

        return extraRows;
    };

    const renderGroup = (perk) => {
        const employees = [];
        const clients = [];

        if (Array.isArray(perk.selectedCompanyRecipients)) {
            perk.selectedCompanyRecipients.forEach(user => {
                if (user.isClient) {
                    clients.push(user);
                } else {
                    employees.push(user);
                }
            });
        }

        if (perk.allEmployeesInPerk) {
            employees.push({ employeeName: "All Employees" });
        }

        if (perk.allClientsInPerk) {
            clients.push({ employeeName: "All Clients" });
        }

        const createParagraph = (text) => (
            <RecipientLabel>
                {text.split("\n").map((line, index) => (
                    <React.Fragment key={index}>
                        {line}
                        {index < text.split("\n").length - 1 && <br />}
                    </React.Fragment>
                ))}
            </RecipientLabel>
        );

        const empLabel = capitalizeString(pluralize(customNames.employees));
        const clientLabel = capitalizeString(pluralize(customNames.clients));

        if (perk.type === PERK_TYPE.GENERAL) {
            if (employees.length > 0 && clients.length > 0) {
                return createParagraph(`${empLabel} & ${clientLabel}`);
            } else if (employees.length > 0) {
                return createParagraph(empLabel);
            } else if (clients.length > 0) {
                return createParagraph(clientLabel);
            }
        }

        if (perk.type === PERK_TYPE.GIFT) {
            let output = "Gift";

            if (employees.length > 0 && clients.length > 0) {
                if (employees.length === 1) {
                    output += ` (${capitalizeString(customNames.employees.slice(0, -1))})\n${employees[0].employeeName}`;
                } else {
                    output += ` (${empLabel})\n${employees.length} ${empLabel}`;
                }

                if (clients.length === 1) {
                    output += `\n(${capitalizeString(customNames.clients.slice(0, -1))})\n${clients[0].employeeName}`;
                } else {
                    output += `\n(${clientLabel})\n${clients.length} ${clientLabel}`;
                }

                return createParagraph(output);
            }

            if (employees.length > 0) {
                if (employees.length === 1) {
                    return createParagraph(`Gift (${capitalizeString(customNames.employees.slice(0, -1))})\n${employees[0].employeeName}`);
                } else {
                    return createParagraph(`Gift (${empLabel})\n${employees.length} ${empLabel}`);
                }
            }

            if (clients.length > 0) {
                if (clients.length === 1) {
                    return createParagraph(`Gift (${capitalizeString(customNames.clients.slice(0, -1))})\n${clients[0].employeeName}`);
                } else {
                    return createParagraph(`Gift (${clientLabel})\n${clients.length} ${clientLabel}`);
                }
            }
        }

        return null;
    };

    const sortPerks = (perks) => {
        if (!sortConfig.key) return perks;

        return [...perks].sort((a, b) => {
            const aValue = sortConfig.key === SORTABLE_COLUMNS.END_DATE ? new Date(a[sortConfig.key]) : a[sortConfig.key]?.toLowerCase();
            const bValue = sortConfig.key === SORTABLE_COLUMNS.END_DATE ? new Date(b[sortConfig.key]) : b[sortConfig.key]?.toLowerCase();

            if (aValue < bValue) return sortConfig.direction === SORT_DIRECTION.ASC ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === SORT_DIRECTION.ASC ? 1 : -1;
            return 0;
        });
    };

    const handleSort = (key) => {
        setSortConfig((prevConfig) => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC,
        }));
    };

    const sortedPerks = sortPerks(perks);

    return (
        <FlexContainer>
            { isFetching && (
                <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    color="#fff"
                    style={{
                        maxWidth: "98.625rem",
                        height: "44.188rem",
                        border: "1px solid #D8DEEC",
                        borderRadius: "16px",
                        background: "#FFFFFF",
                        boxShadow: "0 18px 36px rgba(29, 42, 89, 0.08)"
                    }}
                >
                    <LottieLoadingIndicator />
                </FlexContainer>
            )}

            { !isFetching && (
                <>
                    <PerksUpperControls>
                        <PortalTableControls>
                            <PortalSearchInput placeholder="Search Perk" type="text" value={searchQuery} onChange={handleSearchChange} />
                            <PortalSelect value={filterType} onChange={onHandleFilterByType} width="13rem">
                                <option value="all">All Perks</option>
                                <option value={PERK_TYPE.GENERAL}>General Perks</option>
                                <option value={PERK_TYPE.GIFT}>Gift Perks</option>
                            </PortalSelect>
                            <PortalSelect value={filterStatus} onChange={handleFilterByStatus} width="12rem">
                                <option value={PERK_STATUS.ACTIVE}>Active</option>
                                <option value={PERK_STATUS.SCHEDULED}>Scheduled</option>
                                <option value={PERK_STATUS.EXPIRED}>Expired</option>
                            </PortalSelect>
                            {checkedPerks.length > 0 && (
                                <DeleteButton onClick={() => handleShowDeleteDialog()}>
                                    Delete Selected Perks
                                </DeleteButton>
                            )}
                        </PortalTableControls>

                        <PortalTableActions>
                            <ActivateFeatureButton width="13.125rem" onClick={handleFormVisible}>
                                Create New Perk
                            </ActivateFeatureButton>
                        </PortalTableActions>
                    </PerksUpperControls>

                    <DataTableContainer maxWidth="98.625rem">
                        <DataTable>
                            <thead>
                                <tr>
                                    <DataTableHeaderCell width="4%" textAlign="center">
                                        <label>
                                            <Checkbox
                                                key="selectAll"
                                                readOnly={false}
                                                checked={allPerksChecked}
                                                onChange={handleCheckAllPerks}
                                            />
                                        </label>
                                    </DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%">Image</DataTableHeaderCell>
                                    <DataTableHeaderCell
                                        width="16%"
                                        onClick={() => handleSort(SORTABLE_COLUMNS.COMPANY_NAME)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        Company Name &nbsp;
                                        {sortConfig.key === SORTABLE_COLUMNS.COMPANY_NAME &&
                                            (sortConfig.direction === SORT_DIRECTION.ASC ? <img src={UpArrow} /> : <img src={DownArrow} />)}
                                    </DataTableHeaderCell>
                                    <DataTableHeaderCell width="25%">Heading</DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%">Code</DataTableHeaderCell>
                                    <DataTableHeaderCell
                                        width="9%"
                                        onClick={() => handleSort(SORTABLE_COLUMNS.END_DATE)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        Perk Expiry &nbsp;
                                        {sortConfig.key === SORTABLE_COLUMNS.END_DATE &&
                                            (sortConfig.direction === SORT_DIRECTION.ASC ? <img src={UpArrow} /> : <img src={DownArrow} />)}
                                    </DataTableHeaderCell>
                                    <DataTableHeaderCell width="10%">User Group</DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%" textAlign="center">Insights</DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%" textAlign="center">Edit Perk</DataTableHeaderCell>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedPerks.map(perk => (
                                    <tr key={perk.uuid}>
                                        <DataTableCell textAlign="center" whiteSpace="normal">
                                            <label>
                                                <Checkbox
                                                    readOnly={false}
                                                    checked={checkedPerks.includes(perk.uuid)}
                                                    onChange={(e) => handleCheckPerk(e, perk.uuid)}
                                                />
                                            </label>
                                        </DataTableCell>
                                        <DataTableCell whiteSpace="normal">
                                            <PerkImgContainer>
                                                <PerkImg src={perk.image.imageUrl} />
                                            </PerkImgContainer>
                                        </DataTableCell>
                                        <DataTableCell whiteSpace="normal">{perk.companyName}</DataTableCell>
                                        <DataTableCell whiteSpace="normal"><TableRowCell content={perk.title} clamp={1}/></DataTableCell>
                                        <DataTableCell whiteSpace="normal">{promoType(perk)}</DataTableCell>
                                        <DataTableCell whiteSpace="normal">{formatExpiryDate(perk.endDate)}</DataTableCell>
                                        <DataTableCell whiteSpace="normal">{renderGroup(perk)}</DataTableCell>
                                        <DataTableCell textAlign="center" whiteSpace="normal">
                                            <DataTableViewActionButton
                                                onClick={() => handleShowInsightModal(perk)}
                                            >
                                                View
                                            </DataTableViewActionButton>
                                        </DataTableCell>
                                        <DataTableCell textAlign="center" whiteSpace="normal">
                                            <DataTableEditActionButton
                                                onClick={() => handleEditPerk(perk)}
                                            >
                                                Edit
                                            </DataTableEditActionButton>
                                        </DataTableCell>
                                    </tr>
                                ))}

                                {renderExtraRows()}
                            </tbody>
                        </DataTable>
                    </DataTableContainer>
                </>
            )}

            { showInsightsModal &&
                <InsightModal
                    user={user}
                    perk={selectedPerk}
                    showInsightModal={showInsightsModal}
                    handleCloseInsightModal={handleCloseInsightModal}
                />
            }
        </FlexContainer>
    );
};

export default PerksTable;
